/** A photograph of exactly this property, from the county (§12.2).
 *
 *  Field report 2026-07-25: "It's not auto-mapping trees or proposing what type
 *  of tree it is." The OpenStreetMap lookup cannot do that here — run live
 *  against Greenway Parc 3 it answered "nothing mapped inside this boundary",
 *  because no volunteer has ever mapped that HOA. Aerial photography can.
 *
 *  The Assessor's cached imagery services also expose ArcGIS `export`, which
 *  returns one PNG for an arbitrary bounding box instead of a grid of tiles.
 *  Verified live in the field browser on 2026-07-25: 1024x1024 PNG, 806 KB,
 *  HTTP 200, readable cross-origin. That is one request instead of twenty-five,
 *  and no canvas stitching.
 *
 *  Nothing here measures anything. Geometry is measured from geometry (geo.ts,
 *  Karney); a photograph is only ever used to identify what is growing, and
 *  everything it produces stays unverified until someone walks the site (§2).
 */
import { ServiceError } from '../core/net';
import { inCountyCoverage } from './basemap';
import type { LatLng } from '../core/geo';

/** [south, west, north, east] */
export type BBox = [number, number, number, number];

const SERVICE = (year: number) =>
  `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Imagery/Aerials${year}/MapServer/export`;

/** Longest edge of a requested image. Past this the county downsamples anyway and
 *  the token cost climbs for no extra detail. */
export const MAX_EDGE_PX = 1100;
/** Above this, one frame smears the canopies together — split into quadrants. */
export const QUADRANT_ACRES = 6;

export function ringsBBox(rings: LatLng[][]): BBox {
  let s = 90, w = 180, n = -90, e = -180;
  for (const ring of rings) for (const [lat, lng] of ring) {
    if (lat < s) s = lat; if (lat > n) n = lat;
    if (lng < w) w = lng; if (lng > e) e = lng;
  }
  // strictly greater, not just non-inverted: a degenerate outline (every point
  // identical, or a straight line) gives xmin === xmax, and the county answers a
  // zero-width request with an error image rather than a refusal
  if (!(s < n) || !(w < e)) {
    throw new ServiceError('County imagery', 'guard', 'That outline has no area to photograph. Draw at least three corners.');
  }
  return [s, w, n, e];
}

export function bboxAcresApprox([s, w, n, e]: BBox): number {
  const midLat = (s + n) / 2;
  const mLat = (n - s) * 111_320;
  const mLng = (e - w) * 111_320 * Math.cos((midLat * Math.PI) / 180);
  return (mLat * mLng) / 4046.856;
}

/** Four non-overlapping quadrants, in the order the prompt names them. */
export function quadrants([s, w, n, e]: BBox): { label: string; box: BBox }[] {
  const midLat = (s + n) / 2, midLng = (w + e) / 2;
  return [
    { label: 'north-west', box: [midLat, w, n, midLng] },
    { label: 'north-east', box: [midLat, midLng, n, e] },
    { label: 'south-west', box: [s, w, midLat, midLng] },
    { label: 'south-east', box: [s, midLng, midLat, e] },
  ];
}

/** Pixel size that preserves the ground aspect ratio, longest edge capped. */
export function pixelSize([s, w, n, e]: BBox, maxEdge = MAX_EDGE_PX): [number, number] {
  const midLat = (s + n) / 2;
  const gh = Math.max((n - s) * 111_320, 1);
  const gw = Math.max((e - w) * 111_320 * Math.cos((midLat * Math.PI) / 180), 1);
  const scale = maxEdge / Math.max(gw, gh);
  return [Math.max(64, Math.round(gw * scale)), Math.max(64, Math.round(gh * scale))];
}

export function exportUrl(box: BBox, year: number, size: [number, number]): string {
  const [s, w, n, e] = box;
  const params = new URLSearchParams({
    bbox: `${w},${s},${e},${n}`,   // ArcGIS wants xmin,ymin,xmax,ymax
    bboxSR: '4326',
    imageSR: '3857',
    size: `${size[0]},${size[1]}`,
    format: 'png',
    transparent: 'false',
    f: 'image',
  });
  return `${SERVICE(year)}?${params.toString()}`;
}

export interface AerialFrame {
  label: string;
  mime: string;
  b64: string;
  widthPx: number;
  heightPx: number;
  /** ground centimetres per pixel — context for the reader, never a measurement */
  cmPerPx: number;
}

/** The export is rendered in Web Mercator (imageSR=3857), so latitude does not
 *  map linearly down the image. Over a one-acre tract the error is sub-pixel, but
 *  getting it wrong would silently shift the mask off the ground it is masking. */
const merc = (deg: number) => Math.log(Math.tan(Math.PI / 4 + (deg * Math.PI / 180) / 2));

export function ringToPixels(ring: LatLng[], box: BBox, w: number, h: number): [number, number][] {
  const [s, west, n, e] = box;
  const yN = merc(n), yS = merc(s);
  return ring.map(([lat, lng]) => [
    ((lng - west) / (e - west)) * w,
    ((yN - merc(lat)) / (yN - yS)) * h,
  ]);
}

/** PNG in, JPEG out. The county sends ~800 KB of PNG; the model reads a 200 KB
 *  JPEG identically and the request stays inside the proxy's body limit. */
async function toJpegB64(
  blob: Blob,
  clip?: { box: BBox; rings: LatLng[][] },
  maxEdge = MAX_EDGE_PX,
): Promise<{ b64: string; w: number; h: number }> {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ServiceError('County imagery', 'guard', 'This browser cannot prepare the photo.');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  // Black out everything that is not the client's ground.
  //
  // Field report 2026-07-27: "This is counting all the home parcels too."
  // Greenway Parc 3 is 80 recorded parcels — 78 private houses, one city parcel,
  // and exactly ONE tract the association owns. Reading the whole plat counted
  // 78 people's back yards. Masking is not cosmetic: what the model cannot see,
  // it cannot count.
  if (clip?.rings.length) {
    const path = new Path2D();
    for (const ring of clip.rings) {
      const pts = ringToPixels(ring, clip.box, w, h);
      pts.forEach(([x, y], i) => (i === 0 ? path.moveTo(x, y) : path.lineTo(x, y)));
      path.closePath();
    }
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fill(path);
    // flat backdrop, so the masked area is plainly nothing rather than JPEG noise
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  }
  const url = canvas.toDataURL('image/jpeg', 0.85);
  const comma = url.indexOf(',');
  if (comma < 0) throw new ServiceError('County imagery', 'malformed', 'The photo could not be prepared.');
  return { b64: url.slice(comma + 1), w, h };
}

async function fetchFrame(
  label: string, box: BBox, year: number, signal: AbortSignal, maskRings?: LatLng[][],
): Promise<AerialFrame> {
  const size = pixelSize(box);
  const res = await fetch(exportUrl(box, year, size), { signal });
  if (!res.ok) {
    throw new ServiceError('County imagery', 'http',
      `The county's photo service answered ${res.status}. Try a different flight year, or carry on by hand.`);
  }
  const ctype = res.headers.get('content-type') ?? '';
  if (!ctype.startsWith('image/')) {
    throw new ServiceError('County imagery', 'malformed', 'The county sent something that is not a photograph.');
  }
  const { b64, w, h } = await toJpegB64(await res.blob(), maskRings?.length ? { box, rings: maskRings } : undefined);
  const [, west, , east] = box;
  const midLat = (box[0] + box[2]) / 2;
  const groundM = (east - west) * 111_320 * Math.cos((midLat * Math.PI) / 180);
  return { label, mime: 'image/jpeg', b64, widthPx: w, heightPx: h, cmPerPx: Math.round((groundM / w) * 100) };
}

export interface AerialCapture {
  frames: AerialFrame[];
  acres: number;
  year: number;
  layout: 'single' | 'quadrants';
  /** true when everything outside the client's own ground was blacked out */
  masked: boolean;
}

/**
 * Photograph the property. One frame for a small site, four quadrants for a big
 * one — a whole 20-acre HOA in a single frame smears the canopies into a mat.
 */
export async function captureAerial(
  box: BBox,
  opts: { year: number; signal?: AbortSignal; maskRings?: LatLng[][] },
): Promise<AerialCapture> {
  const [s, w, n, e] = box;
  if (!inCountyCoverage((s + n) / 2, (w + e) / 2)) {
    throw new ServiceError('County imagery', 'guard',
      'The county only photographs Maricopa County. Outside it, add inventory by hand or from site photos.');
  }
  const acres = bboxAcresApprox(box);
  if (acres > 400) {
    throw new ServiceError('County imagery', 'guard',
      `That outline covers about ${Math.round(acres).toLocaleString('en-US')} acres — too much ground to read in one photo. Outline a smaller piece.`);
  }
  const signal = opts.signal ?? AbortSignal.timeout(60000);
  const layout: 'single' | 'quadrants' = acres > QUADRANT_ACRES ? 'quadrants' : 'single';
  const masked = !!opts.maskRings?.length;
  // the label is the only free text the proxy passes straight to the model, so it
  // carries the one thing the model must know about a masked frame
  // The proxy caps a frame label at 40 characters — it was written for
  // "north-west". The instruction the model needs about the mask travels in
  // `place`, which has room for it. A 100-character label here was rejected as
  // invalid_request in production on 2026-07-27.
  const note = masked ? 'client ground only \u2014 ignore black' : 'whole outlined area';
  const label = (base: string) => `${base}`.slice(0, 40);
  const frames = layout === 'single'
    ? [await fetchFrame(label(note), box, opts.year, signal, opts.maskRings)]
    : await Promise.all(quadrants(box).map((q) =>
        fetchFrame(label(masked ? `${q.label} \u2014 client ground only` : q.label), q.box, opts.year, signal, opts.maskRings)));
  return { frames, acres, year: opts.year, layout, masked };
}
